import { OrderType } from '@katanaperps/katana-perps-sdk';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { ApiMarket } from 'api/types';
import Dropdown from 'components/basic/inputs/Dropdown';
import SliderPercentage from 'components/basic/inputs/Slider/SliderPercentage';
import TextInputLabeled, {
  decimalNumberPattern,
} from 'components/basic/inputs/TextInputLabeled';
import { DebugPanel } from 'components/dev/DebugPanel';
import { displayBasicNotification } from 'components/layout/notifications/notificationsUtils';
import {
  buildMarketPair,
  formatNumber,
  formatUsdWithSign,
} from 'lib/display/formatting';
import { isConsideredEmptyOrZero } from 'lib/numbers';
import { debouncerByKey } from 'lib/throttle';
import { useSelectMarketStepSize } from 'reduxState/markets/listed/listedMarketsUtils';
import { useSelector } from 'reduxState/storeCore';

import ActiveTPSLInfo from './ActiveTPSLInfo';
import BuyFieldsAdvanced from './BuyFieldsAdvanced';
import { PanelState, IAction } from './buyPanelReducer';
import {
  BuySellPanelCalculatedInfo,
  BuySellPanelInfoQuantities,
} from './buySellInfo';
import { DevTrigger } from './devDebug';
import { InputPairPanel } from './layout';
import { getOrderMinimum } from './orderCalculations';
import {
  REDUCE_ONLY_AND_TPSL_COMBINE_ERROR,
  tpslChangeHandler,
} from './tpslUtils';
import TriggerTypeDropdown from './TriggerTypeDropdown';

const QuantitiesWrap = styled.div`
  padding-top: 2px;
  border-bottom: 1px solid var(--separator);
  padding-bottom: 14px;
  margin-bottom: 14px;
`;

const MobileDivider = styled.div`
  /*
  width: 100%;
  border-top: 1px solid var(--separator);
  */
`;

type Props = {
  isDisabled: boolean;
  token2quote: string;
  token1base: string;
  panelState: PanelState;
  panelDispatch: (act: IAction) => void;
  getBestPrice: () => string;
  priceDecimals: number;
  quantityDecimals: number;
  quantityBelowMinimum: boolean | undefined;
  marketData?: ApiMarket;
  calculatedInfo: BuySellPanelCalculatedInfo;
  buyingPower: number | undefined;
};

const sliderDebounce = debouncerByKey();

const BuyFields = ({
  isDisabled,
  token2quote,
  token1base,
  panelState,
  panelDispatch,
  getBestPrice,
  priceDecimals,
  quantityDecimals,
  quantityBelowMinimum,
  marketData,
  calculatedInfo,
  buyingPower,
}: Props) => {
  const walletAddress = useSelector(state => state.wallet.walletAddress);
  const isMobile = useSelector(state => state.screen.isMobile);
  const marketSymbol = buildMarketPair(token1base, token2quote);
  const stepSize = useSelectMarketStepSize(marketSymbol);

  const [percentageAmountControl, setPercentageAmountControl] = useState(0);

  useEffect(() => {
    // Sync internal non-throttled state with outside value
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPercentageAmountControl(panelState.sizePercentage || 0);
  }, [panelState.sizePercentage]);

  const handleSetPercentageAmount = (percentageSlider: number) => {
    if (!buyingPower) {
      return;
    }
    setPercentageAmountControl(percentageSlider);
    sliderDebounce('BuyFields:onSetPercentageAmount', 400, () => {
      panelDispatch({
        type: 'set',
        prop: 'sizePercentage',
        value: percentageSlider,
        // TODO: should be last price
        prefillBestPrice: isConsideredEmptyOrZero(panelState.price)
          ? getBestPrice()
          : undefined,
      });
    });
  };

  return (
    <>
      {panelState.triggerEnabled && (
        <InputPairPanel>
          <TextInputLabeled
            pattern={decimalNumberPattern}
            numbericInputMode
            rightAlign
            labelOnLeft="Stop Price"
            label={
              <TriggerTypeDropdown
                selected={panelState.triggerType}
                onChange={value =>
                  panelDispatch({ type: 'changeTriggerType', value })
                }
              />
            }
            required
            disabled={isDisabled || panelState.state !== 'input'}
            onChange={value =>
              panelDispatch({
                type: 'set',
                prop: 'triggerPrice',
                value,
                priceDecimals,
                buyingPower: Number(buyingPower),
              })
            }
            value={panelState.triggerPrice || ''}
          />
        </InputPairPanel>
      )}
      {panelState.orderType === OrderType.limit && (
        <InputPairPanel $noTopPadding>
          <TextInputLabeled
            pattern={decimalNumberPattern}
            numbericInputMode
            rightAlign
            labelOnLeft={<>{panelState.triggerEnabled && 'Order '}Price</>}
            label={token2quote}
            disabled={isDisabled || panelState.state !== 'input'}
            onChange={value =>
              panelDispatch({
                type: 'set',
                prop: 'price',
                value,
                priceDecimals,
                buyingPower: Number(buyingPower),
              })
            }
            value={panelState.price || ''}
          />
        </InputPairPanel>
      )}
      <InputPairPanel $noTopPadding $noBottomPadding>
        <TextInputLabeled
          pattern={panelState.sizePercentage ? undefined : decimalNumberPattern}
          error={
            quantityBelowMinimum
              ? `Quantity does not meet the ${formatNumber(
                  getOrderMinimum(marketData),
                  { fixedDecimals: stepSize?.visibleDecimals }
                )} minimum.`
              : // API Error: Order quantity does not meet the 0.00100000 taker or 0.00100000 maker minimums.
                undefined
          }
          numbericInputMode
          rightAlign
          labelOnLeft={
            <>
              {panelState.sizeInQuote || panelState.sizePercentage
                ? 'Size'
                : 'Quantity'}{' '}
            </>
          }
          label={
            <Dropdown
              selected={panelState.sizeInQuote ? 'quote' : 'base'}
              onChange={value =>
                panelDispatch({
                  type: 'set',
                  prop: 'sizeInQuote',
                  value: value === 'quote',
                })
              }
              options={
                panelState.orderType === 'market' && panelState.triggerEnabled
                  ? [{ label: token1base, value: 'base' }]
                  : [
                      { label: token1base, value: 'base' },
                      { label: token2quote, value: 'quote' },
                    ]
              }
              noBorder
            />
          }
          disabled={isDisabled || panelState.state !== 'input'}
          onChange={value =>
            panelDispatch({
              type: 'set',
              prop: 'size',
              value,
              quantityDecimals,
              prefillBestPrice:
                isConsideredEmptyOrZero(panelState.price) &&
                isConsideredEmptyOrZero(panelState.size)
                  ? getBestPrice()
                  : undefined,
            })
          }
          value={
            panelState.sizePercentage
              ? `${panelState.sizePercentage}%`
              : panelState.size || ''
          }
        />
      </InputPairPanel>
      <div style={{ height: 4 }} />
      {!(panelState.orderType === 'market' && panelState.triggerEnabled) && (
        <SliderPercentage
          inactive={!percentageAmountControl}
          value={percentageAmountControl}
          onChange={handleSetPercentageAmount}
          disabled={
            !buyingPower
            // We prefill best price (TODO: should be middle or no ?) For now we disable it until we have price available
            // // (panelState.orderType === 'limit' && !panelState.price)
          }
          min={0}
          max={100}
        />
      )}
      {isMobile ? (
        <MobileDivider />
      ) : (
        <QuantitiesWrap>
          <BuySellPanelInfoQuantities
            token1base={token1base}
            token2quote={token2quote}
            calculatedInfo={calculatedInfo}
          />
        </QuantitiesWrap>
      )}
      <BuyFieldsAdvanced
        isDisabled={isDisabled}
        isLimitOrder={panelState.orderType === 'limit'}
        hasStop={panelState.triggerEnabled}
        reduceOnly={panelState.reduceOnly}
        onChangeReduceOnly={reduceOnly => {
          if (panelState.conditionalTpSl) {
            displayBasicNotification(
              REDUCE_ONLY_AND_TPSL_COMBINE_ERROR,
              `form_buySellTpslReduceOnly_${Date.now()}`,
              'error'
            );
            return;
          }
          panelDispatch({ type: 'set', prop: 'reduceOnly', value: reduceOnly });
        }}
        postOnly={panelState.postOnly}
        onChangePostOnly={postOnly =>
          panelDispatch({ type: 'set', prop: 'postOnly', value: postOnly })
        }
        timeInForce={panelState.limitOrderTimeInForce}
        onChangeTimeInForce={timeInForce =>
          panelDispatch({
            type: 'changeLimitOrderTimeInForce',
            value: timeInForce,
          })
        }
        conditionalTpSl={!!panelState.conditionalTpSl}
        onChangeConditionalTpSlModal={check =>
          tpslChangeHandler(check, {
            walletAddress,
            panelState,
            panelDispatch,
            calculatedInfo,
          })
        }
      />
      {panelState.conditionalTpSl && (
        <ActiveTPSLInfo
          conditionalTpSl={panelState.conditionalTpSl}
          market={marketSymbol}
          onEditTPSL={() => {
            tpslChangeHandler(true, {
              walletAddress,
              panelState,
              panelDispatch,
              calculatedInfo,
            });
          }}
        />
      )}

      {!isMobile && (
        <DebugPanel fixedBottomRight bottomMargin={24}>
          {panelState.triggerEnabled && (
            <DevTrigger
              triggerPrice={Number(panelState.triggerPrice)}
              triggerType={panelState.triggerType}
              indexPrice={marketData?.indexPrice}
            />
          )}
          Buying power = {formatUsdWithSign(buyingPower)}{' '}
          <small>(Note: Slider max is 97%)</small>
        </DebugPanel>
      )}
    </>
  );
};

export default BuyFields;
